/**
 * oracle's implementation of the payment ENGINE's transport.
 *
 * Deliberately much smaller than `apps/web/src/payments/transport.ts` (v2's,
 * 277 lines), and the difference is the point: v2's `persist` DECIDES which of
 * four destinations a commit goes to, inside app-only code that no node check
 * can reach — which is why §10b's five missing behaviours were found by reading
 * v1 rather than by a test. Here `saleDomain.buildCommit` has already decided,
 * and this file is a switch on the name plus the host-data stamping that
 * genuinely cannot leave the app.
 *
 * The provider-specific calls (device config, card search, OTP) are NOT on the
 * engine's transport interface — a provider casts to the slice it needs via
 * `providerTransport`, so adding one never widens this file's contract.
 */
import store from "redux-store/index";
import { closeSaleFn, saleCheckoutSave } from "facade/ServiceFacade";
import {
  checkForOtp,
  checkForProviderBalance,
  fetchRistaCards,
  getExtLinkForBalance,
  getExtStatus,
  refundPayment,
  saleReturnClose,
  setRistaCardList,
  updateCancelPayment,
  updatePaymentClose,
  updateSalePayment,
  validateRistaCard,
} from "redux-store/slices/CheckoutSlice";
import { fetchDeviceConfig } from "redux-store/slices/LocalPaymentInfo";
import { discountHelpers } from "@dotpe/rista-pos-common";
import { checkCreateTicket, fetchCustomerOtpHash, verifyOtpHash } from "helper/saleHelpers";
import { ENDPOINT } from "config/endpoints";
import { QWIKCILVER_PAYMENT_TYPES } from "data/constants";
import { validateSaleData } from "helper/customerAndDeliveryInfoValidation";
import { CREATE_TICKETS, CREATE_TICKET_ON_CLOSE, CREATE_TICKET_ON_SAVE, PHONEPE_SQR } from "utils/constants";
import { toSaleLeg } from "@rista/payments/domains/sale";
import type { SaleCommit } from "@rista/payments/domains/sale";
import type { EngineTransport, Leg, ProviderStatus } from "@rista/payments/engine/types";

/** the app's dispatch-with-callback convention, as a promise */
const dispatched = <T,>(action: (payload: any) => any, payload: any): Promise<T> =>
  new Promise((resolve) => store.dispatch(action({ ...payload, callback: resolve })));

/**
 * Close-only host data. All three are close-only in v1: they live inside
 * `handleCheckoutAction` and neither `handleSaveOrder` nor the auto-save path
 * touches them, so applying them to a save would send approval requests v1
 * never sends on that endpoint.
 */
function stampForClose(sale: any): any {
  const next = { ...sale };
  next[CREATE_TICKETS] = checkCreateTicket(CREATE_TICKET_ON_CLOSE);
  if (next.requestOrderApproval) delete next.requestOrderApproval;
  const approvalData = discountHelpers.getCurrentDiscountApprovalData(
    store.getState().discountStore?.discountApproval,
    next.mid || ""
  )?.[0]?.approvalData;
  if (approvalData && Object.keys(approvalData).length) {
    next.approvalRequests = Array.isArray(approvalData) ? approvalData : [approvalData];
  }
  return next;
}

/**
 * The body all three provider calls share — v1's `CheckoutContainer.getDataStruc:861`,
 * which `fetchExternalPaymentStatus`, `cancelExt` and `refundExt` each post whole.
 *
 * It is NOT the leg. The provider's own blob rides under `data.externalPaymentData`
 * with its `deviceId` lifted out beside it, and the leg's own fields become
 * top-level params — so a flat spread of `leg.data` reaches this endpoint with
 * none of the keys it reads.
 *
 * Built from `toSaleLeg`, so every read below is v1's read verbatim: the engine
 * splits v1's one flat payment across `leg` and `leg.data`, and a provider's
 * token lands on either half (`_auth` writes both). The wire leg is where they
 * are one object again.
 *
 * `mode === PHONEPE_SQR` decides the transaction key inline, as v1 does (`:866`):
 * one provider, one line, and no `Provider` row widened for it.
 */
function extStatusPayload(leg: Leg) {
  const payment = toSaleLeg(leg) as any;
  const data: any = { paymentId: payment.paymentId };

  if (payment.mode === PHONEPE_SQR) {
    data.transactionSuffix =
      payment.data?.transactionSuffix || payment.transactionSuffix || payment.transactionId;
  } else {
    data.transactionId = payment.transactionId;
  }
  data.qrData = payment.qrData;
  data.deviceId = payment.data?.deviceId;
  data.externalPaymentData = payment.data;
  data.reason = payment.reason;

  return {
    saleId: store.getState().saleDataStore?.saleData?.mid,
    pid: payment.pid,
    amount: payment.requestAmount,
    mid: payment.mode,
    data,
    // v1 reads `extras` FLAT while paytmdqr writes it INSIDE `data`
    // (`paytmDQRComp:90`), so this is undefined on every DQR call. v1's own
    // bug, mirrored rather than corrected — see docs §18.
    extras: payment.extras,
    ...(payment.img ? { poweredByImage: payment.img } : {}),
  };
}

export const webTransport: EngineTransport = {
  /**
   * Requesting an external payment POSTS THE WHOLE SALE (docs §10) — a real
   * persist, because the backend needs a saved sale to hang the provider
   * transaction off, and it is what lets a requested leg survive a reload.
   */
  initiate(payload: unknown): Promise<unknown> {
    return dispatched(updateSalePayment, { postBody: payload });
  },

  /**
   * NO GLOBAL LOADER on the automatic poll. The runner re-polls every 10s for as long as the
   * payment is with its provider, so the loader the interceptor puts up by default would blink
   * over the screen on a timer — on the kiosk, over the QR the customer is mid-scan. The poll
   * is background work the operator never asked for, and the screen already shows the leg's
   * own state.
   *
   * The `retry` variant KEEPS it: that one is the operator pressing "Check status", it may
   * RE-RUN the charge rather than read it, and a press with no feedback reads as a dead button.
   */
  pollStatus(leg: Leg, opts?: { retry?: boolean }): Promise<ProviderStatus> {
    const postBody: any = extStatusPayload(leg);
    if (opts?.retry) {
      // v1's refresh-icon variant: the server may RE-RUN the charge, not read it
      postBody.retry = true;
      // the WIRE leg, not the engine's — v1 sends the payment as it sits on
      // the sale (`paymentToFetch`), nested `data` and all
      postBody.paymentRecData = toSaleLeg(leg);
    }
    return dispatched(getExtStatus, { postBody, skipGlobalLoader: !opts?.retry });
  },

  cancel(leg: Leg): Promise<ProviderStatus> {
    return dispatched(updateCancelPayment, { postBody: extStatusPayload(leg) });
  },

  refund(leg: Leg): Promise<ProviderStatus> {
    return dispatched(refundPayment, { postBody: extStatusPayload(leg) });
  },

  /**
   * The single write. The DESTINATION was already chosen by
   * `saleDomain.routeCommit` and is pinned by `domains/sale/routing.test.ts`;
   * this only dispatches it and stamps the host data.
   */
  persist(commit: unknown): Promise<unknown> {
    const { destination, body, mid, closing } = commit as SaleCommit;

    // `updatePaymentClose` posts the paymentInfo ALONE — the domain already
    // shaped the body, so there is nothing to stamp and nothing to validate
    if (destination === "updatePaymentClose") {
      return dispatched(updatePaymentClose, { postBody: body, mid });
    }

    const sale = closing ? stampForClose(body) : { ...body, createTickets: checkCreateTicket(CREATE_TICKET_ON_SAVE) };
    const validated = validateSaleData(sale);

    switch (destination) {
      case "save":
        return new Promise((resolve) => saleCheckoutSave({ postBody: validated, callback: resolve }));
      case "saleReturnClose":
        return dispatched(saleReturnClose, { postBody: validated });
      case "close":
      default:
        // offline-aware: an offline close never reaches axios, it goes to
        // IndexedDB (`ServiceFacade.closeSaleFn:454`)
        return new Promise((resolve) => closeSaleFn({ postBody: validated, callback: resolve }));
    }
  },
};

/**
 * Provider-specific calls, merged in by the container — see `providerTransport`.
 *
 * TWO payload shapes, which is the app's own convention and not something to
 * normalise: some of these sagas take `{ postBody, callback }` and the
 * provider-scoped ones take `{ dataToSend: { postBody, callback }, providerId }`.
 */
export const webProviderTransport = {
  /**
   * Uniform for all four device providers — `{ pid }` to
   * `INTEGRATEDPAYMENT.getPaytmEdc(pid)` (`pineLabsComp:46`, `bonusHubComp:51`,
   * `paytmDQRComp:36`, `paytmEdcComp:27` all build exactly this). The response
   * is provider-shaped and comes back RAW; each provider's own
   * `readDeviceConfig` normalises it.
   *
   * The `url` is NOT optional — the saga has no endpoint without it.
   */
  deviceConfig: (pid: string) =>
    new Promise((resolve) =>
      store.dispatch(
        fetchDeviceConfig({
          url: ENDPOINT.INTEGRATEDPAYMENT.getPaytmEdc(pid ?? ""),
          postBody: { pid },
          callback: resolve,
        })
      )
    ),

  /**
   * The account balance. The body is provider-shaped: gyftr identifies the
   * account by the customer's number (`ExternalPaymentProvider/index.tsx:38`),
   * qwikcilver by a typed card number + PIN and a GIFTCARD mode
   * (`QuickCilver/qwikcilver.tsx:104`).
   */
  checkBalance: (input: { pid?: string; cardNumber?: string; mode?: string }) => {
    const sale = store.getState().saleDataStore?.saleData;
    const postBody: any = {
      mobileNumber: sale?.customerInfo?.contactInfo?.phoneNumber,
      amount: sale?.billTotal,
    };
    if (input.cardNumber) {
      postBody.cardNumber = input.cardNumber;
      postBody.mode = QWIKCILVER_PAYMENT_TYPES.GIFTCARD;
    }
    return new Promise((resolve) =>
      store.dispatch(
        checkForProviderBalance({
          dataToSend: { postBody, callback: resolve },
          providerId: input.pid,
        })
      )
    );
  },

  /** gyftr only — a link the customer can top the account up with (`:62`). */
  getTopUpLink: (pid?: string) => {
    const sale = store.getState().saleDataStore?.saleData;
    return new Promise((resolve) =>
      store.dispatch(
        getExtLinkForBalance({
          dataToSend: {
            postBody: { mobileNumber: sale?.customerInfo?.contactInfo?.phoneNumber },
            callback: resolve,
          },
          providerId: pid,
        })
      )
    );
  },

  /** gyftr only — the PROVIDER sends the OTP (`:132`). Distinct from Rista's own. */
  sendProviderOtp: (pid?: string) => {
    const sale = store.getState().saleDataStore?.saleData;
    return new Promise((resolve) =>
      store.dispatch(
        checkForOtp({
          dataToSend: {
            postBody: { mobileNumber: sale?.customerInfo?.contactInfo?.phoneNumber },
            callback: resolve,
          },
          providerId: pid,
        })
      )
    );
  },

  /**
   * Search the merchant's Rista cards. `field` IS the request key
   * (`RistaCardComp:106`); resolves the card list, empty if none or on error.
   *
   * Not `dispatched()`: `fetchRistaCardsData` (`saga/checkout/checkout.js:463`)
   * never calls its callback, it PUTs the list into redux — so this waits on
   * `ristaCardList`, where v1 reads it (`RistaCardComp:25`).
   *
   * The wait is on `pending`, a marker only this call can produce, so any
   * `setRistaCardList` settles it — every path through the saga ends in one.
   * Seeding it also clears the previous results, as v1's `resetSearch` does.
   */
  searchCards: (query: string, field: string): Promise<any[]> =>
    new Promise((resolve) => {
      const pending: any[] = [];
      store.dispatch(setRistaCardList(pending));
      const unsubscribe = store.subscribe(() => {
        const list = store.getState().checkoutModalStore?.ristaCardList;
        if (list === pending) return;
        unsubscribe();
        resolve(list ?? []);
      });
      store.dispatch(fetchRistaCards({ postBody: { [field]: query, pageSize: 20 } }));
    }),

  /** Rista Card — validate before initiating (`RistaCardComp:136`). */
  validateCard: (ristaCard: unknown, record: any) =>
    dispatched(validateRistaCard, { postBody: { ristaCard, sale: record?.sale } }),

  /** Prepaid — Rista's OWN OTP to the customer, not a provider's. */
  sendCustomerOtp: () => Promise.resolve(fetchCustomerOtpHash()),

  /** Prepaid — SYNCHRONOUS by design: v1's `verifyOtpHash` checks a hash it holds. */
  verifyCustomerOtp: (code: string) => !!verifyOtpHash(code),
};
